import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';

interface NotificationPreference {
  user_uuid: string;
  enabled: boolean;
  updated_at: string | null;
  updated_by_name: string | null;
}

interface UserNotificationPreferenceTabProps {
  userUuid: string;
}

export default function UserNotificationPreferenceTab({ userUuid }: UserNotificationPreferenceTabProps) {
  const [preference, setPreference] = useState<NotificationPreference | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchPreference = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<NotificationPreference>(
        `/admin/users/${userUuid}/notification-preference`
      );
      setPreference(data);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreference();
  }, [userUuid]);

  const handleToggle = async (enabled: boolean) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const data = await apiClient.put<NotificationPreference>(
        `/admin/users/${userUuid}/notification-preference`,
        { enabled }
      );
      setPreference(data);
      setSuccess(`Fog notifications ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ p: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={preference?.enabled ?? false}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={saving}
            />
          }
          label={
            <Box>
              <Typography>Receive fog notifications</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                When enabled, this user will receive email notifications when low visibility fog is detected at monitored airports.
              </Typography>
            </Box>
          }
        />
      </Box>
    </Box>
  );
}
